import React, { useState } from 'react';
import { X, Lock, Mail, User as UserIcon, Shield, CheckCircle2 } from 'lucide-react';
import { api, setSessionToken } from '../../services/api';
import { User } from '../../types';
import styles from './AuthModal.module.css';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  onRequire2FA: (userId: string) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onRequire2FA,
}) => {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [identity, setIdentity] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const res = await api.login({ identity, password, rememberMe });
        if (res.require2FA) {
          onRequire2FA(res.userId);
          onClose();
        } else {
          setSessionToken(res.token, res.csrfToken);
          localStorage.setItem('cordle_user', JSON.stringify(res.user));
          onSuccess(res.user);
          onClose();
        }
      } else if (mode === 'register') {
        const res = await api.register({ username, email, password, displayName, rememberMe });
        setSessionToken(res.token, res.csrfToken);
        localStorage.setItem('cordle_user', JSON.stringify(res.user));
        setMessage(res.message);
        setTimeout(() => {
          onSuccess(res.user);
          onClose();
        }, 1200);
      } else if (mode === 'forgot') {
        const res = await api.forgotPassword(email);
        setMessage(res.message);
      }
    } catch (err: any) {
      setError(err.message || 'Authentication request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>
          <X size={20} />
        </button>

        <h2 className={styles.title}>
          {mode === 'login' && 'Welcome Back'}
          {mode === 'register' && 'Join Cordle'}
          {mode === 'forgot' && 'Account Recovery'}
        </h2>
        <p className={styles.subtitle}>
          {mode === 'login' && 'Enter your credentials to access your Cordle account.'}
          {mode === 'register' && 'Create your unique handle and customizable profile.'}
          {mode === 'forgot' && 'Enter your email to receive a password reset token.'}
        </p>

        {mode !== 'forgot' && (
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${mode === 'login' ? styles.activeTab : ''}`}
              onClick={() => { setMode('login'); setError(''); setMessage(''); }}
            >
              Sign In
            </button>
            <button
              className={`${styles.tab} ${mode === 'register' ? styles.activeTab : ''}`}
              onClick={() => { setMode('register'); setError(''); setMessage(''); }}
            >
              Register
            </button>
          </div>
        )}

        {error && <div className={styles.errorBox}>{error}</div>}
        {message && <div className={styles.successBox}>{message}</div>}

        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>Username (@handle)</label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="alex_dev"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Display Name</label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Alex Rivers"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email Address</label>
                <input
                  type="email"
                  className={styles.input}
                  placeholder="alex@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          {mode === 'login' && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Email or Username</label>
              <input
                type="text"
                className={styles.input}
                placeholder="cordle_user or email@domain.com"
                value={identity}
                onChange={(e) => setIdentity(e.target.value)}
                required
              />
            </div>
          )}

          {mode === 'forgot' && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Account Email</label>
              <input
                type="email"
                className={styles.input}
                placeholder="your.email@domain.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          )}

          {mode !== 'forgot' && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Password</label>
              <input
                type="password"
                className={styles.input}
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          )}

          {mode === 'login' && (
            <div className={styles.checkboxGroup}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <span
                style={{ color: 'var(--accent-primary)', cursor: 'pointer' }}
                onClick={() => { setMode('forgot'); setError(''); setMessage(''); }}
              >
                Forgot password?
              </span>
            </div>
          )}

          <button type="submit" className={styles.submitBtn} disabled={loading}>
            {loading ? 'Authenticating...' : mode === 'login' ? 'Sign In to Cordle' : mode === 'register' ? 'Create Account' : 'Send Recovery Email'}
          </button>
        </form>

        {mode === 'forgot' && (
          <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '0.88rem' }}>
            <span
              style={{ color: 'var(--accent-primary)', cursor: 'pointer' }}
              onClick={() => { setMode('login'); setError(''); setMessage(''); }}
            >
              Back to Sign In
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
